import { useEffect, useMemo, useState } from "react";

const CONFETTI_COLORS = [
  "#f59e0b",
  "#fbbf24",
  "#fcd34d",
  "#10b981",
  "#34d399",
  "#6ee7b7",
  "#f472b6",
  "#fb7185",
  "#fda4af",
  "#d97706",
  "#f97316",
  "#fb923c",
  "#84cc16",
  "#a3e635",
  "#eab308",
  "#facc15",
  "#14b8a6",
  "#2dd4bf",
  "#ec4899",
  "#f43f5e",
];

const PIECE_COUNT = 120;
const SESSION_KEY = "confetti_clinic_search_played";

interface Piece {
  id: number;
  color: string;
  size: number;
  shape: "square" | "circle";
  phase1X: number;
  phase1Y: number;
  phase1R: number;
  phase2X: number;
  phase2Y: number;
  phase2R: number;
  delay: number;
  duration: number;
}

function randomBetween(min: number, max: number): number {
  return Math.random() * (max - min) + min;
}

function generatePieces(): Piece[] {
  return Array.from({ length: PIECE_COUNT }, (_, i) => {
    const angle = randomBetween(0, Math.PI * 2);
    const burstDistance = randomBetween(80, 420);
    const burstX = Math.cos(angle) * burstDistance;
    const burstY = Math.sin(angle) * burstDistance - randomBetween(40, 160);

    const driftX = burstX + randomBetween(-180, 180);
    const fallY = burstY + randomBetween(300, 700) + Math.abs(burstX) * 0.4;

    return {
      id: i,
      color:
        CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      size: randomBetween(5, 13),
      shape: Math.random() > 0.4 ? "square" : "circle",
      phase1X: burstX,
      phase1Y: burstY,
      phase1R: randomBetween(-200, 200),
      phase2X: driftX,
      phase2Y: fallY,
      phase2R: randomBetween(-540, 540),
      delay: randomBetween(0, 0.35),
      duration: randomBetween(2.2, 3.8),
    } as Piece;
  });
}

export default function ConfettiBurst() {
  const [visible, setVisible] = useState(false);
  const [cleaned, setCleaned] = useState(false);
  const [messageVisible, setMessageVisible] = useState(false);

  const pieces = useMemo(() => generatePieces(), []);

  useEffect(() => {
    const played = sessionStorage.getItem(SESSION_KEY);
    if (played) return;

    sessionStorage.setItem(SESSION_KEY, "1");

    const appearTimer = setTimeout(() => {
      setVisible(true);
      setMessageVisible(true);
    }, 300);
    const messageFadeTimer = setTimeout(() => setMessageVisible(false), 3000);
    const cleanupTimer = setTimeout(() => setCleaned(true), 4600);

    return () => {
      clearTimeout(appearTimer);
      clearTimeout(messageFadeTimer);
      clearTimeout(cleanupTimer);
    };
  }, []);

  if (cleaned) return null;

  return (
    <div
      className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden"
      aria-hidden="true"
    >
      {/* Celebration toast message */}
      <div
        className="absolute top-[22%] left-1/2 -translate-x-1/2"
        style={{
          opacity: messageVisible ? 1 : 0,
          transform: messageVisible
            ? "translate(-50%, 0) scale(1)"
            : "translate(-50%, 8px) scale(0.95)",
          transition: "opacity 0.5s ease-out, transform 0.5s ease-out",
        }}
      >
        <div className="bg-white/95 backdrop-blur-md rounded-2xl px-6 py-3.5 shadow-[0_8px_32px_rgba(0,0,0,0.12)] border border-accent-200/60">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
              <span className="w-5 h-5 flex items-center justify-center text-accent-600">
                <i className="ri-check-double-line text-lg"></i>
              </span>
            </span>
            <div>
              <p className="text-sm font-bold text-foreground-900 whitespace-nowrap">
                You did it!
              </p>
              <p className="text-[11px] text-foreground-500 whitespace-nowrap">
                The clinic will reach out within 24 hours
              </p>
            </div>
          </div>
        </div>
      </div>

      {visible &&
        pieces.map((p) => (
          <span
            key={p.id}
            className="absolute"
            style={{
              left: "50%",
              top: "38%",
              width: `${p.size}px`,
              height: `${p.shape === "square" ? p.size : p.size * 1.6}px`,
              backgroundColor: p.color,
              borderRadius: p.shape === "circle" ? "9999px" : "2px",
              animationName: "confettiBurstPiece",
              animationDuration: `${p.duration}s`,
              animationDelay: `${p.delay}s`,
              animationTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)",
              animationFillMode: "forwards",
              opacity: 0,
              ["--phase1-x" as string]: `${p.phase1X}px`,
              ["--phase1-y" as string]: `${p.phase1Y}px`,
              ["--phase1-r" as string]: `${p.phase1R}deg`,
              ["--phase2-x" as string]: `${p.phase2X}px`,
              ["--phase2-y" as string]: `${p.phase2Y}px`,
              ["--phase2-r" as string]: `${p.phase2R}deg`,
            }}
          />
        ))}
    </div>
  );
}
